import React, { useState, useEffect } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import api from "../../../../../utils/apiConfig";

import logger from '../../../../../utils/logger';

const StageTooltip = ({ stage, isComplete, isCurrent }) => (
  <div className="absolute bottom-full mb-2 w-48 bg-surface-default dark:bg-surface-default shadow-lg dark:shadow-none rounded-lg p-3 text-sm transform -translate-x-1/2 left-1/2 z-10 invisible group-hover:visible transition-all duration-200">
    <div className="font-semibold mb-1">{stage.stage_name}</div>
    <div className="text-gray-600 dark:text-gray-400 text-xs">
      {isComplete
        ? "✓ Stage Complete"
        : isCurrent
          ? "Current Stage"
          : "Not Started"}
    </div>
    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-surface-default dark:bg-surface-default"></div>
  </div>
);

const StatusTooltip = ({ status }) => (
  <div className="absolute bottom-full mb-2 w-64 bg-surface-default dark:bg-surface-default shadow-lg dark:shadow-none rounded-lg p-3 text-sm transform -translate-x-1/2 left-1/2 z-10 invisible group-hover:visible transition-all duration-200">
    <div className="font-semibold mb-1">{status.status_name}</div>
    <div className="text-gray-600 dark:text-gray-400 text-xs">{status.status_description}</div>
    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-surface-default dark:bg-surface-default"></div>
  </div>
);

const PipelineCard = ({ householdId, userId, clientData }) => {
  const [stages, setStages] = useState([]);
  const [pipelineStage, setPipelineStage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPipelineData = async () => {
      if (!householdId || !userId) return;

      try {
        setLoading(true);
        setError(null);

        const [stagesResponse, statusResponse] = await Promise.all([
          api.get(
            `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}/pipeline/stages`,
          ),
          api.get(
            `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}/pipeline`,
          ),
        ]);

        setStages(stagesResponse.data || []);
        setPipelineStage(statusResponse.data || null);
      } catch (error) {
        logger.error("Error fetching pipeline data:", error);
        setError("Failed to load pipeline data");
      } finally {
        setLoading(false);
      }
    };

    fetchPipelineData();
  }, [householdId, userId]);

  if (loading) {
    return (
      <div className="w-full bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
          <div className="flex justify-between items-center">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                <div className="h-2 bg-gray-200 rounded w-16 mt-2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
        {error}
      </div>
    );
  }

  // Find current stage based on stage_id from pipeline status
  const currentStage = Array.isArray(stages)
    ? stages.find((stage) => stage.stage_id === pipelineStage?.stage_id)
    : null;
  const currentStageIndex = Array.isArray(stages)
    ? stages.findIndex((stage) => stage.stage_id === pipelineStage?.stage_id)
    : -1;

  // Calculate overall progress
  const percentComplete =
    currentStageIndex >= 0
      ? (((currentStageIndex + 1) / (stages?.length || 1)) * 100).toFixed(0)
      : 0;

  // Calculate status progress within current stage
  const currentStatus = currentStage?.statuses?.find(
    (status) => status.status_id === pipelineStage?.status_id,
  );
  const currentStatusIndex =
    currentStage?.statuses?.findIndex(
      (status) => status.status_id === pipelineStage?.status_id,
    ) ?? -1;

  // If no stages are loaded yet, show a message
  if (!Array.isArray(stages) || stages.length === 0) {
    return (
      <div className="w-full bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none p-6">
        <div className="text-center text-gray-500 dark:text-gray-500">
          No pipeline stages available
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold">Pipeline Progress</h3>
          <div className="relative group">
            <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full cursor-help">
              {pipelineStage?.stage_name || "Unknown"}:{" "}
              {pipelineStage?.status_name || "Unknown"}
            </span>
            {pipelineStage?.status_description && (
              <div className="absolute bottom-full left-0 mb-2 w-64 bg-surface-default dark:bg-surface-default shadow-lg dark:shadow-none rounded-lg p-3 text-sm invisible group-hover:visible transition-all duration-200 z-20">
                <div className="font-semibold mb-1">
                  {pipelineStage.status_name}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-xs">
                  {pipelineStage.status_description}
                </div>
                <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-surface-default dark:bg-surface-default"></div>
              </div>
            )}
          </div>
        </div>
        <div className="text-sm font-medium text-gray-500 dark:text-gray-500">
          Stage {currentStageIndex + 1} of {stages.length}
        </div>
      </div>

      {/* Stage progress */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Overall Progress
          </span>
          <span className="text-sm font-medium text-blue-600">
            {percentComplete}% Complete
          </span>
        </div>
        <div className="relative w-full h-2 bg-gray-200 rounded-full">
          <div
            className="absolute h-full bg-blue-600 rounded-full transition-all duration-500 ease-in-out"
            style={{ width: `${percentComplete}%` }}
          />
        </div>
      </div>

      {/* Stage indicators */}
      <div className="relative flex justify-between items-center mb-8">
        {stages.map((stage, index) => {
          const isComplete = index < currentStageIndex;
          const isCurrent = index === currentStageIndex;

          return (
            <div key={stage.stage_id} className="relative group">
              <StageTooltip
                stage={stage}
                isComplete={isComplete}
                isCurrent={isCurrent}
              />
              <div
                className={`
                  w-10 h-10 rounded-full border-2 flex items-center justify-center
                  transition-all duration-300 cursor-pointer
                  ${
                    isComplete
                      ? "bg-blue-500 border-blue-600 text-white"
                      : isCurrent
                        ? "bg-surface-default dark:bg-surface-default border-blue-500 text-blue-500"
                        : "bg-surface-default dark:bg-surface-default border-gray-300 text-gray-400"
                  }
                  ${isCurrent ? "ring-4 ring-blue-100" : ""}
                `}
              >
                {index + 1}
              </div>
              <div className="text-xs text-center mt-2 text-gray-600 dark:text-gray-400">
                {stage.stage_name}
              </div>
              {index < stages.length - 1 && (
                <div
                  className={`
                    absolute top-5 left-full w-full h-0.5 -translate-y-1/2
                    ${index < currentStageIndex ? "bg-blue-500" : "bg-gray-300"}
                  `}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Current stage status indicators */}
      {currentStage?.statuses && currentStage.statuses.length > 0 && (
        <div className="border-t pt-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Current Stage Progress
            </span>
            <div className="relative group">
              <span className="text-sm font-medium text-blue-600 cursor-help">
                {pipelineStage?.status_name || "Unknown"}
              </span>
              {pipelineStage?.status_description && (
                <div className="absolute bottom-full right-0 mb-2 w-64 bg-surface-default dark:bg-surface-default shadow-lg dark:shadow-none rounded-lg p-3 text-sm invisible group-hover:visible transition-all duration-200 z-20">
                  <div className="font-semibold mb-1">
                    {pipelineStage.status_name}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400 text-xs">
                    {pipelineStage.status_description}
                  </div>
                  <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-surface-default dark:bg-surface-default"></div>
                </div>
              )}
            </div>
          </div>
          <div className="relative flex justify-between items-center">
            {currentStage.statuses.map((status, index) => {
              const isComplete = index < currentStatusIndex;
              const isCurrent = index === currentStatusIndex;

              return (
                <div key={status.status_id} className="relative group">
                  <StatusTooltip status={status} />
                  <div
                    className={`
                      w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm
                      transition-all duration-300 cursor-pointer
                      ${
                        isComplete
                          ? "bg-green-500 border-green-600 text-white"
                          : isCurrent
                            ? "bg-surface-default dark:bg-surface-default border-green-500 text-green-500"
                            : "bg-surface-default dark:bg-surface-default border-gray-300 text-gray-400"
                      }
                      ${isCurrent ? "ring-2 ring-green-100" : ""}
                    `}
                  >
                    {index + 1}
                  </div>
                  {index < currentStage.statuses.length - 1 && (
                    <div
                      className={`
                        absolute top-4 left-full w-full h-0.5 -translate-y-1/2
                        ${index < currentStatusIndex ? "bg-green-500" : "bg-gray-300"}
                      `}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineCard;
